import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';
import { label } from '../../shared/labels';
import { LIMIT_CODES, ListSkeleton, renderFailure } from '../../shared/states';
import { ago } from '../../shared/time';
import { useLoad } from '../../shared/use-load';
import { useUpgrade } from '../../shared/upgrade';
import { openLink } from '../../telegram/webapp';
import { CallSeller } from '../calls/CallSeller';
import { PriceHistory } from './PriceHistory';

/**
 * Карточка объявления (T11.8, `09_UI_UX.md` §4.4, `02_SPEC.md` §9).
 *
 * ## Порядок задан документом, и он не случаен
 *
 * Галерея → цена с отклонением → характеристики → свежесть → объяснение
 * оценки → история цены → другие площадки → признаки из описания →
 * кнопки. Это порядок вопросов, которые человек задаёт себе, глядя на
 * машину: что это, почём, что за комплектация, не протухло ли, почему
 * вы считаете это выгодным, дешевеет ли, где ещё продают, нет ли
 * подвоха.
 *
 * История цены появится в T11.9 и встанет между объяснением оценки и
 * другими площадками — место под неё оставлено намеренно, а не забыто.
 *
 * ## «Открыть оригинал» не прокручивается
 *
 * Требование задачи и L-4: главное действие доступно всегда. Оно стоит
 * в закреплённой полосе внизу, а не в конце страницы. Карточка длинная,
 * и кнопка в её хвосте означала бы, что до основного действия надо
 * доскроллить, — то есть что мы прячем уход на площадку.
 *
 * ## Три запроса, а не один
 *
 * Карточка, оценка и другие площадки идут порознь и приходят порознь.
 * Оценка требует своих доводов и своего времени, а сравнение площадок
 * платное и на бесплатном тарифе отказывает. Свести их в один ответ
 * значило бы задержать показ машины до готовности самого медленного и
 * уронить карточку целиком из-за платной части.
 */

interface Reference {
  readonly id: number;
  readonly name: string;
}

interface Spec {
  readonly body: string;
  readonly engine_type: string;
  readonly engine_volume_cc: number | null;
  readonly power_hp: number | null;
  readonly gearbox: string;
  readonly drive: string;
}

export interface Card {
  readonly listing_id: string | number;
  readonly status: 'active' | 'removed' | 'sold' | 'expired';
  readonly source: { id: number; code: string; name: string };
  readonly url: string;
  readonly brand: Reference | null;
  readonly model: Reference | null;
  readonly generation: Reference | null;
  readonly spec: Spec | null;
  readonly spec_id: number | null;
  readonly year: number;
  readonly mileage_km: number | null;
  readonly owners_count: number | null;
  readonly has_vin: boolean;
  readonly seller_type: string;
  readonly price: {
    minor: string | number;
    currency: string;
    previous_minor: string | number | null;
    changed_at: string | null;
  };
  readonly region: Reference;
  readonly city: Reference | null;
  readonly published_at: string;
  readonly published_at_precision: 'exact' | 'day' | 'observed';
  readonly first_seen_at: string;
  readonly last_seen_at: string;
  readonly images: readonly { position: number; url: string }[];
  readonly market_delta_pct: number | null;
  /**
   * Отклонение от медианы своей области, своего федерального округа и
   * по стране.
   *
   * Три цифры, а не одна: «дешевле на 12 %» отвечает на половину
   * вопроса — дешевле чего? В своей области машина может стоить как все
   * и быть заметно дешевле, чем по стране, и наоборот. Пусто — выборка
   * этого уровня мала: областная рассыпается раньше окружной, окружная
   * — раньше общероссийской.
   */
  readonly market_delta_region_pct: number | null;
  readonly market_delta_district_pct: number | null;
  readonly market_delta_country_pct: number | null;
  readonly is_favorite: boolean;
  readonly description_features: {
    damage_noted: boolean;
    customs_unpaid_noted: boolean;
    duplicate_title_noted: boolean;
  };
}

interface Group {
  readonly year: number;
  readonly mileage_from_km: number;
  readonly mileage_to_km: number | null;
  readonly region_id: number | null;
}

type Estimate =
  | {
      readonly available: true;
      readonly scope: 'region' | 'country';
      readonly sample_size: number;
      readonly median_minor: number;
      readonly p25_minor: number;
      readonly p75_minor: number;
      readonly delta_pct: number | null;
      readonly group: Group;
      readonly calculated_at: string;
    }
  | { readonly available: false; readonly reason: string; readonly min_sample: number };

interface Duplicate {
  readonly listing_id: string | number;
  readonly source: { id: number; code: string; name: string };
  readonly url: string;
  readonly status: string;
  readonly price_minor: string | number;
  readonly price_delta_minor: number;
  readonly mileage_km: number | null;
}

function rubles(minor: string | number): string {
  return `${Math.round(Number(minor) / 100).toLocaleString('ru-RU')} ₽`;
}

function kilometres(km: number): string {
  return `${km.toLocaleString('ru-RU')} км`;
}

/**
 * Метка свежести.
 *
 * Слово выбирается по точности отметки. Drom времени публикации не
 * отдаёт вовсе, и там честно «замечено»: выдать наш обход за
 * публикацию значило бы обещать раннее обнаружение там, где мы его не
 * знаем.
 */
function freshness(card: Card): string {
  if (card.published_at_precision === 'observed') return `Замечено ${ago(card.first_seen_at)}`;
  if (card.published_at_precision === 'day') {
    return `Опубликовано ${new Date(card.published_at).toLocaleDateString('ru-RU')}`;
  }

  return `Опубликовано ${ago(card.published_at)}`;
}

const STATUS_WORDS: Record<string, string> = {
  removed: 'Объявление снято с публикации',
  sold: 'Автомобиль продан',
  expired: 'Срок размещения истёк',
};

/**
 * Галерея: лента с прилипанием.
 *
 * Пальцем — пролистыванием, и на телефоне этого достаточно. Мышью
 * пролистать горизонтальную ленту нечем: колесо прокручивает страницу,
 * а перетаскивания в браузере нет. Отсюда две кнопки по краям снимка.
 *
 * Они невидимы, пока указатель не над галереей, и появляются только на
 * устройствах с наведением (`@media (hover: hover)`): на телефоне они
 * не нужны и лишь закрывали бы часть кадра. Совсем без вида оставить их
 * нельзя — управление, о котором нельзя догадаться, всё равно что
 * отсутствует.
 */
function Gallery({
  images,
}: {
  images: readonly { position: number; url: string }[];
}): React.JSX.Element {
  const strip = useRef<HTMLDivElement>(null);

  const nudge = useCallback((direction: -1 | 1) => {
    const node = strip.current;

    if (node === null) return;

    // На ширину видимой части, а не на фиксированное число точек:
    // снимок занимает почти всю ширину, и прокрутка ровно на неё
    // попадает в следующую точку прилипания
    node.scrollBy({ left: direction * node.clientWidth * 0.9, behavior: 'smooth' });
  }, []);

  if (images.length === 0) {
    return <div className="gallery gallery--empty">Снимков нет</div>;
  }

  return (
    <div className="gallery">
      <div className="gallery__strip" ref={strip}>
        {images.map((image) => (
          <img
            key={image.position}
            className="gallery__shot"
            src={image.url}
            alt=""
            loading="lazy"
          />
        ))}
      </div>

      {images.length < 2 ? null : (
        <>
          <button
            type="button"
            className="gallery__step gallery__step--prev"
            aria-label="Предыдущий снимок"
            onClick={() => {
              nudge(-1);
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="gallery__step gallery__step--next"
            aria-label="Следующий снимок"
            onClick={() => {
              nudge(1);
            }}
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}

/** Пара «что» и «сколько». */
function Fact({ title, value }: { title: string; value: string }): React.JSX.Element {
  return (
    <div className="fact">
      <span className="fact__title">{title}</span>
      <span className="fact__value">{value}</span>
    </div>
  );
}

function facts(card: Card): { title: string; value: string }[] {
  const rows: { title: string; value: string }[] = [{ title: 'Год', value: String(card.year) }];

  if (card.mileage_km !== null) rows.push({ title: 'Пробег', value: kilometres(card.mileage_km) });

  if (card.spec !== null) {
    const engine = [
      card.spec.engine_volume_cc === null
        ? null
        : `${(card.spec.engine_volume_cc / 1000).toFixed(1)} л`,
      label(card.spec.engine_type),
      card.spec.power_hp === null ? null : `${String(card.spec.power_hp)} л.с.`,
    ].filter((one): one is string => one !== null);

    rows.push(
      { title: 'Двигатель', value: engine.join(', ') },
      { title: 'Коробка', value: label(card.spec.gearbox) },
      { title: 'Привод', value: label(card.spec.drive) },
      { title: 'Кузов', value: label(card.spec.body) },
    );
  }

  if (card.owners_count !== null) {
    rows.push({ title: 'Владельцев', value: String(card.owners_count) });
  }

  rows.push(
    { title: 'VIN', value: card.has_vin ? 'указан' : 'не указан' },
    { title: 'Продавец', value: label(card.seller_type) },
    { title: 'Где', value: card.city?.name ?? card.region.name },
    { title: 'Площадка', value: card.source.name },
  );

  return rows;
}

/**
 * Объяснение оценки (`02_SPEC.md` §10).
 *
 * Число без объяснения запрещено, и здесь оно объясняется тем же, чем
 * его считали: медианой, размером выборки и границами группы. Человек
 * должен иметь возможность пойти на площадку и пересчитать.
 */
function Estimation({ estimate }: { estimate: Estimate }): React.JSX.Element {
  if (!estimate.available) {
    return (
      <div className="card block">
        <p className="block__title">Оценка рынка</p>
        <p className="block__hint">
          Таких автомобилей пока мало: для оценки нужно хотя бы {estimate.min_sample} объявлений.
          Как только наберётся, посчитаю.
        </p>
      </div>
    );
  }

  const { group } = estimate;
  const mileage =
    group.mileage_to_km === null
      ? `от ${kilometres(group.mileage_from_km)}`
      : `${group.mileage_from_km.toLocaleString('ru-RU')}–${kilometres(group.mileage_to_km)}`;

  return (
    <div className="card block">
      <p className="block__title">Почему такая оценка</p>

      <p className="block__lead">
        {estimate.delta_pct === null
          ? `Медиана — ${rubles(estimate.median_minor)}`
          : `${estimate.delta_pct > 0 ? '+' : '−'}${String(Math.abs(estimate.delta_pct))} % к медиане ${rubles(estimate.median_minor)}`}
      </p>

      <p className="block__hint">
        Считал по {estimate.sample_size} объявлениям
        {estimate.scope === 'country' ? ' по стране' : ' в этом регионе'}: {String(group.year)} год,
        пробег {mileage}. Половина таких стоит от {rubles(estimate.p25_minor)} до{' '}
        {rubles(estimate.p75_minor)}.
      </p>
    </div>
  );
}

/**
 * Другие площадки (T8.6).
 *
 * Ради этого блока и затевалась склейка: одна машина в трёх местах с
 * разными ценами — самый убедительный довод за подписку. На бесплатном
 * тарифе показывается именно это, а не отказ: человек должен видеть,
 * что закрыто, ровно тогда, когда оно ему нужно.
 */
function Elsewhere({
  duplicates,
  locked,
}: {
  duplicates: readonly Duplicate[];
  locked: boolean;
}): React.JSX.Element | null {
  if (locked) {
    return (
      <div className="card block">
        <p className="block__title">Эта же машина на других площадках</p>
        <p className="block__hint">
          Одно и то же авто нередко выставлено дважды и с разной ценой. Сравнение по площадкам
          открывается на Pro.
        </p>
      </div>
    );
  }

  if (duplicates.length === 0) return null;

  return (
    <div className="card block">
      <p className="block__title">Эта же машина на других площадках</p>

      {duplicates.map((one) => (
        <button
          key={`${String(one.source.id)}:${String(one.listing_id)}`}
          type="button"
          className="elsewhere"
          onClick={() => {
            openLink(one.url);
          }}
        >
          <span className="elsewhere__source">{one.source.name}</span>
          <span className="elsewhere__price">{rubles(one.price_minor)}</span>
          {one.price_delta_minor === 0 ? null : (
            <span
              className={
                one.price_delta_minor < 0
                  ? 'elsewhere__delta elsewhere__delta--good'
                  : 'elsewhere__delta'
              }
            >
              {one.price_delta_minor < 0 ? '−' : '+'}
              {rubles(Math.abs(one.price_delta_minor))}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Признаки из описания (T5.7).
 *
 * Сам текст не выдаётся (L-3), но три вывода из него — выдаются, и они
 * стоят дороже остального: битая машина и неоплаченная растаможка
 * меняют решение целиком.
 */
function Warnings({ card }: { card: Card }): React.JSX.Element | null {
  const found = [
    card.description_features.damage_noted ? 'в описании упомянуты повреждения' : null,
    card.description_features.customs_unpaid_noted ? 'упомянута неоплаченная растаможка' : null,
    card.description_features.duplicate_title_noted ? 'упомянут дубликат ПТС' : null,
  ].filter((one): one is string => one !== null);

  if (found.length === 0) return null;

  return (
    <div className="card block block--warning">
      <p className="block__title">На что посмотреть</p>
      <ul className="block__list">
        {found.map((one) => (
          <li key={one}>{one}</li>
        ))}
      </ul>
      <p className="block__hint">
        Найдено в тексте объявления автоматически. Проверьте у продавца.
      </p>
    </div>
  );
}

/**
 * Звезда: отложить объявление и убрать обратно (T7.7).
 *
 * Состояние приходит с карточкой (`is_favorite`), а не спрашивается
 * отдельно: кнопка рисуется вместе с ней, и запоздавший ответ второго
 * запроса означал бы, что человек успел нажать «отложить» на том, что
 * уже отложено, — то есть снял бы отметку, полагая, что ставит.
 *
 * Вид меняется по ответу сервера, а не по нажатию. Заманчиво
 * перекрасить звезду сразу — так делают почти все, — но здесь это
 * означало бы «отложено» на объявлении, которого в избранном нет:
 * список открывается в двух нажатиях, и расхождение видно сразу.
 */
function Favorite({ card }: { card: Card }): React.JSX.Element {
  const [kept, setKept] = useState(card.is_favorite);
  const [busy, setBusy] = useState(false);
  const id = String(card.listing_id);

  const flip = useCallback(() => {
    setBusy(true);

    const done = (next: boolean): void => {
      setKept(next);
      setBusy(false);
    };

    if (kept) {
      request(`/v1/favorites/by-listing/${id}`, { method: 'DELETE' })
        .then(() => {
          done(false);
        })
        .catch(() => {
          setBusy(false);
        });

      return;
    }

    request('/v1/favorites', { method: 'POST', body: { listing_id: id } })
      .then(() => {
        // Повтор ошибкой не считается: сервер отвечает 200 и на уже
        // отложенное (T7.7), и итог в обоих случаях один
        done(true);
      })
      .catch(() => {
        setBusy(false);
      });
  }, [id, kept]);

  return (
    <button
      type="button"
      className={kept ? 'button actions__keep actions__keep--on' : 'button actions__keep'}
      disabled={busy}
      aria-pressed={kept}
      aria-label={kept ? 'Убрать из избранного' : 'В избранное'}
      onClick={flip}
    >
      {kept ? '★' : '☆'}
    </button>
  );
}

/**
 * Отклонение от рынка двумя строками.
 *
 * Показывается только то, что посчитано: пустое отклонение означает,
 * что похожих машин в выборке меньше десяти, а оценка по трём
 * наблюдениям хуже отсутствия оценки — человек поедет смотреть за свои
 * деньги и своё время.
 *
 * Ноль не прячется, в отличие от крупной цифры у цены: «столько же,
 * сколько по стране» — это ответ, а не отсутствие ответа, и именно он
 * отличает обычную машину от той, чью цену мы просто не знаем.
 */
function MarketDeltas({
  region,
  district,
  country,
}: {
  region: number | null;
  district: number | null;
  country: number | null;
}): React.JSX.Element | null {
  // Порядок от точного к общему — тот же, каким база выбирает главную
  // цифру. Пустые уровни просто не показываются: у большинства машин
  // областной выборки нет, и строка «по области: неизвестно» сообщила
  // бы человеку только о нашем устройстве
  const parts: string[] = [
    region === null ? '' : `${words(region)} по области`,
    district === null ? '' : `${words(district)} по округу`,
    country === null ? '' : `${words(country)} по стране`,
  ].filter((part) => part !== '');

  if (parts.length === 0) return null;

  return <p className="listing__market">{parts.join(' · ')}</p>;
}

/** «Дешевле на 12 %», «дороже на 5 %», «столько же». */
function words(delta: number): string {
  if (delta === 0) return 'столько же';

  return delta < 0 ? `дешевле на ${String(-delta)} %` : `дороже на ${String(delta)} %`;
}

export function ListingScreen({
  listingId,
  onBack,
}: {
  listingId: string | number;
  onBack: () => void;
}): React.JSX.Element {
  const id = String(listingId);
  const [state, retry] = useLoad<Card>(
    useCallback(() => request<Card>(`/v1/listings/${id}`), [id]),
  );
  const onUpgrade = useUpgrade();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [duplicates, setDuplicates] = useState<readonly Duplicate[]>([]);
  const [locked, setLocked] = useState(false);

  // Просмотр отмечается по открытию, а не по кнопке: человек открыл
  // карточку — значит, объявление он видел, и в выдаче оно должно
  // приглушиться. Отказ отметки на показ не влияет
  useEffect(() => {
    void request(`/v1/listings/${id}/seen`, { method: 'POST' }).catch(() => undefined);
  }, [id]);

  useEffect(() => {
    let alive = true;

    request<Duplicate[]>(`/v1/listings/${id}/duplicates`)
      .then((found) => {
        if (alive) setDuplicates(found);
      })
      .catch((failure: unknown) => {
        // Платная часть: отказ по тарифу — не поломка, а повод показать,
        // что именно закрыто
        if (alive) setLocked(failure instanceof ApiFailure && LIMIT_CODES.has(failure.code));
      });

    return () => {
      alive = false;
    };
  }, [id]);

  const card = state.kind === 'ready' ? state.value : null;

  useEffect(() => {
    let alive = true;

    // Оценка — свойство группы, а не объявления, и без модификации либо
    // пробега группы не существует. Спросить наугад значило бы получить
    // отказ проверки доводов и показать его как «мало данных»
    if (card === null || card.spec_id === null || card.mileage_km === null) return undefined;

    const query = new URLSearchParams({
      spec_id: String(card.spec_id),
      year: String(card.year),
      mileage_km: String(card.mileage_km),
      region_id: String(card.region.id),
      price_minor: String(Number(card.price.minor)),
    });

    request<Estimate>(`/v1/market/estimate?${query.toString()}`)
      .then((found) => {
        if (alive) setEstimate(found);
      })
      .catch(() => {
        // Объяснение — довесок к числу, которое уже показано. Его отказ
        // не повод портить карточку
        if (alive) setEstimate(null);
      });

    return () => {
      alive = false;
    };
  }, [card]);

  if (state.kind === 'loading') return <ListSkeleton rows={3} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade })}</>;
  }

  const one = state.value;
  const name = [one.brand?.name, one.model?.name, one.generation?.name]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' ');
  const previous = one.price.previous_minor;
  const delta = one.market_delta_pct;

  return (
    <>
      {/* Над полосой действий: низ карточки занят «Открыть оригинал» */}
      <button type="button" className="back back--above-actions" onClick={onBack}>
        ← К результатам
      </button>

      <Gallery images={one.images} />

      {one.status === 'active' ? null : (
        <p className="notice">{STATUS_WORDS[one.status] ?? 'Объявление неактивно'}</p>
      )}

      <h2 className="listing__name">
        {name === '' ? 'Автомобиль' : name}, {one.year}
      </h2>

      <div className="listing__price">
        {rubles(one.price.minor)}
        {delta === null || delta === 0 ? null : (
          <span className={delta < 0 ? 'result__delta result__delta--good' : 'result__delta'}>
            {delta > 0 ? '+' : '−'}
            {Math.abs(delta)} % к рынку
          </span>
        )}
      </div>

      {/*
        Обе цифры под ценой, а не вместо неё. Крупная показывает
        главное — дешевле или дороже, — а строка ниже отвечает на
        вопрос «чего именно дешевле»: своего города или страны целиком.
        Расхождение между ними и есть самое полезное, что мы можем
        сказать: машина, дешёвая по стране и обычная в своей области, —
        это не находка, а местная цена.
      */}
      <MarketDeltas
        region={one.market_delta_region_pct}
        district={one.market_delta_district_pct}
        country={one.market_delta_country_pct}
      />

      {previous === null ? null : (
        <p className="listing__was">
          Было {rubles(previous)}
          {one.price.changed_at === null ? '' : ` · ${ago(one.price.changed_at)}`}
        </p>
      )}

      <div className="card facts">
        {facts(one).map((row) => (
          <Fact key={row.title} title={row.title} value={row.value} />
        ))}
      </div>

      <p className="listing__fresh">{freshness(one)}</p>

      {estimate === null ? null : <Estimation estimate={estimate} />}

      {/* После объяснения оценки и до других площадок — так задан
          порядок в `09_UI_UX.md` §4.4 */}
      <PriceHistory listingId={one.listing_id} />

      <Elsewhere duplicates={duplicates} locked={locked} />

      <Warnings card={one} />

      {/* После предупреждений: звонок — следующий шаг, когда
          объявление выглядит стоящим (T18.8) */}
      {one.status === 'active' ? (
        <CallSeller sourceId={one.source.id} listingId={String(one.listing_id)} />
      ) : null}

      <div className="actions__space" />

      {/*
        Закреплённая полоса, а не кнопка в конце страницы. Главное
        действие обязано быть доступно без прокрутки — это требование
        задачи, и оно же честность: мы не прячем уход на площадку.
      */}
      <div className="actions">
        <Favorite card={one} />
        <button
          type="button"
          className="button button--accent actions__main"
          onClick={() => {
            openLink(one.url);
          }}
        >
          Открыть оригинал
        </button>
      </div>
    </>
  );
}
